import { Injectable, inject } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { injectReportsAppKind, injectReportsTranslations } from '@core/reports-contracts';
import {
  DataInterval,
  DetailsBreadcrumbsParams,
  DetailsBreadcrumbsService,
  ReportsAppKind,
  ReportsFilterBreadcrumb,
} from '@core/reports-types';
import { formatDateWithInterval } from '@core/reports-ui';

/**
 * A net change drilldown always carries two coordinates: the row (a period for
 * the over time flavour, a group value when split by a dimension) and the side
 * of the pair that was clicked - joiners or leavers. The latter is rendered the
 * same way funnel renders its segment: a plain bold crumb.
 */
@Injectable()
export class DetailsNetChangeBreadcrumbsService implements DetailsBreadcrumbsService {
  private i18n = inject(I18nService);
  private reportsTranslations = injectReportsTranslations();
  private clampEndToToday = injectReportsAppKind({ optional: true }) === ReportsAppKind.Ats;

  public getBreadcrumbs({
    breadcrumbsSettings,
    dimensions,
  }: DetailsBreadcrumbsParams): ReportsFilterBreadcrumb[] {
    const breadcrumbs: ReportsFilterBreadcrumb[] = [];
    const interval = <DataInterval | undefined>breadcrumbsSettings.settings?.interval;

    if (interval && breadcrumbsSettings.dateFilter) {
      const date = formatDateWithInterval(
        dimensions.primary.name,
        breadcrumbsSettings.dateFilter,
        interval,
        this.clampEndToToday,
      );

      breadcrumbs.push({
        simple: true,
        bold: true,
        translatedValue: `${date.primary}, ${date.secondary}`,
        hasOverwrites: false,
      });
    } else if (breadcrumbsSettings.primaryGroup) {
      breadcrumbs.push({
        simple: false,
        translatedGroup: this.reportsTranslations.translateGroup(breadcrumbsSettings.primaryGroup),
        translatedValues: [{ label: dimensions.primary.name }],
        hasOverwrites: false,
        invalid: false,
      });
    }

    if (dimensions.secondary.length > 0) {
      breadcrumbs.push({
        simple: true,
        bold: true,
        translatedValue:
          dimensions.secondary.length > 1
            ? this.i18n.translate('tlr.group_name.other')
            : dimensions.secondary[0].name,
        hasOverwrites: false,
      });
    }

    return breadcrumbs;
  }
}
